import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';

// HubSpot contact property the PDF link is sent in (then synced on to
// Salesforce by HubSpot). The form itself must contain it as a hidden
// field — HubSpot silently drops values for fields not on the form.
export const PDF_URL_PROPERTY = 'roi_pdf_customer';

// If neither embed has reported "ready" by then, it was most likely
// blocked (cookie consent, content blocker) rather than just slow.
const LOAD_TIMEOUT_MS = 15000;

const scriptPromises = {};

// Each embed script only ever needs loading once per page, even if the
// gate re-mounts or more than one calculator is on the page.
const loadScript = ( src ) => {
	if ( ! scriptPromises[ src ] ) {
		scriptPromises[ src ] = new Promise( ( resolve, reject ) => {
			const script = document.createElement( 'script' );
			script.src = src;
			script.async = true;
			script.onload = resolve;
			script.onerror = () => {
				delete scriptPromises[ src ];
				reject( new Error( `Could not load ${ src }` ) );
			};
			document.body.appendChild( script );
		} );
	}

	return scriptPromises[ src ];
};

const hubspotScriptHost = ( region ) =>
	region && region !== 'na1'
		? `https://js-${ region }.hsforms.net`
		: 'https://js.hsforms.net';

// Legacy-editor forms render their own React inputs; setting `.value`
// directly would be overwritten on their next render, so go through the
// native setter + events the way a real keystroke would.
const setLegacyHiddenField = ( form, name, value ) => {
	const formElement = form?.jquery ? form[ 0 ] : form;
	const input = formElement?.querySelector?.( `input[name="${ name }"]` );

	if ( ! input ) {
		// eslint-disable-next-line no-console
		console.warn(
			`ROI Calculator: the HubSpot form has no hidden "${ name }" field — the PDF link will not reach HubSpot.`
		);
		return;
	}

	// The form usually lives in an iframe — use that window's own classes.
	const frameWindow = input.ownerDocument.defaultView;

	Object.getOwnPropertyDescriptor(
		frameWindow.HTMLInputElement.prototype,
		'value'
	).set.call( input, value );
	input.dispatchEvent( new frameWindow.Event( 'input', { bubbles: true } ) );
	input.dispatchEvent( new frameWindow.Event( 'change', { bubbles: true } ) );
};

const embedLegacyForm = ( { leadForm, target, onReady, onSubmitted } ) =>
	( window.hbspt?.forms
		? Promise.resolve()
		: loadScript(
				`${ hubspotScriptHost( leadForm.region ) }/forms/embed/v2.js`
		  )
	).then( () =>
		window.hbspt.forms.create( {
			region: leadForm.region,
			portalId: leadForm.portalId,
			formId: leadForm.formId,
			target: `#${ target.id }`,
			onFormReady: onReady,
			onFormSubmitted: onSubmitted,
		} )
	);

// New-editor forms: the embed script scans the page for `.hs-form-frame`
// elements (rendered below) and reports back through global events.
const embedNewForm = ( { leadForm, onReady, onSubmitted } ) => {
	const isThisForm = ( event ) => event.detail?.formId === leadForm.formId;
	const handleReady = ( event ) => isThisForm( event ) && onReady( event );
	const handleSubmitted = ( event ) => isThisForm( event ) && onSubmitted();

	window.addEventListener( 'hs-form-event:on-ready', handleReady );
	window.addEventListener(
		'hs-form-event:on-submission:success',
		handleSubmitted
	);

	return {
		loaded: loadScript(
			`${ hubspotScriptHost( leadForm.region ) }/forms/embed/${
				leadForm.portalId
			}.js`
		),
		cleanup: () => {
			window.removeEventListener( 'hs-form-event:on-ready', handleReady );
			window.removeEventListener(
				'hs-form-event:on-submission:success',
				handleSubmitted
			);
		},
	};
};

/**
 * Stands in for the calculator's results until the visitor has submitted
 * the calculator's HubSpot form (see Settings.php's `leadForm`). The PDF
 * link is put into the form's hidden `roi_pdf_customer` field as soon as
 * the form is ready — before the PDF exists: component.js stores it at
 * exactly that URL right after a successful submission (PdfRoutes.php).
 *
 * @param {Object}   props
 * @param {Object}   props.leadForm    { portalId, formId, region, editor }.
 * @param {string}   props.pdfUrl      Public URL the PDF will be stored at.
 * @param {Function} props.onSubmitted Called once the form was submitted.
 */
const LeadFormGate = ( { leadForm, pdfUrl, onSubmitted } ) => {
	const containerRef = useRef( null );
	const [ status, setStatus ] = useState( 'loading' );
	// The embed callbacks are registered once; keep them pointed at the
	// latest props instead of the ones from the first render.
	const latest = useRef( { pdfUrl, onSubmitted } );
	latest.current = { pdfUrl, onSubmitted };

	const isLegacy = leadForm.editor === 'legacy';

	useEffect( () => {
		let isMounted = true;
		const timeout = window.setTimeout( () => {
			if ( isMounted ) {
				setStatus( ( current ) =>
					current === 'loading' ? 'error' : current
				);
			}
		}, LOAD_TIMEOUT_MS );
		const markReady = () => isMounted && setStatus( 'ready' );
		const markFailed = () => isMounted && setStatus( 'error' );
		const handleSubmitted = () => latest.current.onSubmitted();

		if ( isLegacy ) {
			embedLegacyForm( {
				leadForm,
				target: containerRef.current,
				onReady: ( form ) => {
					setLegacyHiddenField(
						form,
						PDF_URL_PROPERTY,
						latest.current.pdfUrl
					);
					markReady();
				},
				onSubmitted: handleSubmitted,
			} ).catch( markFailed );

			return () => {
				isMounted = false;
				window.clearTimeout( timeout );
			};
		}

		const { loaded, cleanup } = embedNewForm( {
			leadForm,
			onReady: ( event ) => {
				// "0-1" = the contact object, in the new editor's field naming.
				Promise.resolve(
					window.HubSpotFormsV4?.getFormFromEvent(
						event
					)?.setFieldValue(
						`0-1/${ PDF_URL_PROPERTY }`,
						latest.current.pdfUrl
					)
				).catch( () =>
					// eslint-disable-next-line no-console
					console.warn(
						`ROI Calculator: could not set the HubSpot form's hidden "${ PDF_URL_PROPERTY }" field — the PDF link will not reach HubSpot.`
					)
				);
				markReady();
			},
			onSubmitted: handleSubmitted,
		} );
		loaded.catch( markFailed );

		return () => {
			isMounted = false;
			window.clearTimeout( timeout );
			cleanup();
		};
		// The form config never changes for a mounted calculator.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	return (
		<div className="roi-calculator__step roi-calculator__lead-form">
			<h3>{ __( 'Get your results', 'sprintray-roi-calculator' ) }</h3>
			<p className="roi-calculator__lead-form-intro">
				{ __(
					'Fill in the form to see your personal calculation and download it as a PDF.',
					'sprintray-roi-calculator'
				) }
			</p>
			{ status === 'error' && (
				<p className="roi-calculator__lead-form-error" role="alert">
					{ __(
						'The form could not be loaded. Please check your cookie settings or disable content blockers, then reload the page.',
						'sprintray-roi-calculator'
					) }
				</p>
			) }
			{ isLegacy ? (
				<div
					id={ `roi-calculator-lead-form-${ leadForm.formId }` }
					ref={ containerRef }
				/>
			) : (
				<div
					ref={ containerRef }
					className="hs-form-frame"
					data-region={ leadForm.region }
					data-form-id={ leadForm.formId }
					data-portal-id={ leadForm.portalId }
				/>
			) }
		</div>
	);
};

LeadFormGate.propTypes = {
	leadForm: PropTypes.shape( {
		portalId: PropTypes.string.isRequired,
		formId: PropTypes.string.isRequired,
		region: PropTypes.string,
		editor: PropTypes.oneOf( [ 'new', 'legacy' ] ),
	} ).isRequired,
	pdfUrl: PropTypes.string.isRequired,
	onSubmitted: PropTypes.func.isRequired,
};

export default LeadFormGate;
